import { Page } from '@dixa/lib-shared-habla';
import { AsyncStatus, Satisfaction } from 'lib-shared-legacy-types';
import { useMount } from 'react-use';

import { Language } from 'app/utils/supportedLanguages';

import Languages from '../../../../../../containers/sections/satisfaction/languages';
import { ChatGlobalLevel } from '../../../../../../reducers/sections/satisfaction';
import { GlobalCsatSwitch } from '../../common/GlobalCsatSwitch';
import { Csat } from '../../common/types';
import { ChatCSAT } from '../types';

export interface LanguageSetting extends Language {
  settings: Satisfaction.ChatCsatSettings | null;
}

interface StateProps {
  globalSettings: ChatGlobalLevel;
  languagesSettings: {
    list: LanguageSetting[];
    asyncStatus: AsyncStatus;
  };
}

interface DispatchProps {
  loadSettingsAllLevels: () => void;
  createGlobalSettings: (
    settings: Satisfaction.CreateChatCsatSettingsParams,
  ) => void;
  updateGlobalSettings: (
    settings: Satisfaction.UpdateChatCsatSettingsParams,
  ) => void;
  updateLanguageLevelSettings: (
    settings: Partial<Satisfaction.ChatCsatSettings>,
  ) => void;
  initializeChatCsat: () => void;
}

type Props = DispatchProps & StateProps;

export const ChatPanel = (props: Props): JSX.Element => {
  useMount(() => {
    props.loadSettingsAllLevels();
  });

  const getEnrichedLanguagesWithSettings = (): ChatCSAT.Language[] => {
    return props.languagesSettings.list.map(
      ({ code, label, settings }): ChatCSAT.Language => ({
        isSetup: settings !== null,
        ratingStyle: settings?.customization.look_and_feel,
        enabled: settings?.enabled || false,
        name: label,
        code,
        settings,
      }),
    );
  };

  const handleGlobalStatusChange = (change: Csat.CsatStatusChange) => {
    // If we have global settigns already, update it
    if (props.globalSettings.values) {
      props.updateGlobalSettings({
        ...props.globalSettings.values,
        enabled: change.newStatus,
      });
    } else {
      // Otherwise csat is not initialized , so initialize if needed.
      if (change.newStatus) {
        props.initializeChatCsat();
      }
    }
  };

  const handleLanguageStatusChange = ({ language, newStatus }) => {
    // the default language should not be allowed to be disabled.
    if (props.globalSettings?.values?.language === language.code) {
      return;
    }

    const payload: Partial<Satisfaction.ChatCsatSettings> = {
      id: language.settings?.id,
      enabled: newStatus,
      channel: 'widgetchat',
    };

    props.updateLanguageLevelSettings(payload);
  };

  const { globalSettings } = props;
  const isActive = globalSettings.values?.enabled || false;

  return (
    <Page.Section
      callout={
        <GlobalCsatSwitch
          onChange={handleGlobalStatusChange}
          channel="widgetchat"
          isActive={isActive}
        />
      }
    >
      <Languages
        languages={getEnrichedLanguagesWithSettings()}
        onLanguageStatusChange={handleLanguageStatusChange}
      />
    </Page.Section>
  );
};
